import { useEffect, useState } from "preact/hooks";

type AssetStatus = "idle" | "loading" | "ready" | "error";

type AssetOptions = {
  enabled?: boolean;
};

export function useExternalScript(src: string | null | undefined, options: AssetOptions = {}) {
  const enabled = options.enabled ?? true;
  const [status, setStatus] = useState<AssetStatus>(() =>
    !src || !enabled ? "idle" : getScriptStatus(src),
  );

  useEffect(() => {
    if (!src || !enabled) {
      setStatus("idle");
      return;
    }

    const scriptSrc = src;
    let cancelled = false;
    setStatus(getScriptStatus(src));

    async function loadScript() {
      try {
        await loadExternalScript(scriptSrc);
        if (!cancelled) setStatus("ready");
      } catch {
        if (!cancelled) setStatus("error");
      }
    }

    loadScript();

    return () => {
      cancelled = true;
    };
  }, [src, enabled]);

  return status;
}

export function useExternalStyle(href: string | null | undefined, options: AssetOptions = {}) {
  const enabled = options.enabled ?? true;

  useEffect(() => {
    if (!href || !enabled) return;

    loadExternalStyle(href);
  }, [href, enabled]);
}

export function loadExternalScript(src: string) {
  const existing = document.querySelector<HTMLScriptElement>(`script[src="${cssEscape(src)}"]`);
  if (existing?.dataset.status === "ready") return Promise.resolve();

  if (existing?.dataset.status === "loading") return waitForScript(existing);

  const script = existing ?? document.createElement("script");
  script.src = src;
  script.async = true;
  script.dataset.status = "loading";

  const promise = waitForScript(script);
  if (!existing) document.head.appendChild(script);

  return promise;
}

export function loadExternalStyle(href: string) {
  const existing = document.querySelector<HTMLLinkElement>(`link[href="${cssEscape(href)}"]`);
  if (existing) return existing;

  const link = document.createElement("link");
  link.href = href;
  link.rel = "stylesheet";
  link.type = "text/css";
  document.head.appendChild(link);
  return link;
}

function getScriptStatus(src: string): AssetStatus {
  return (
    (document.querySelector<HTMLScriptElement>(`script[src="${cssEscape(src)}"]`)?.dataset
      .status as AssetStatus) ?? "idle"
  );
}

function waitForScript(script: HTMLScriptElement) {
  return new Promise<void>((resolve, reject) => {
    script.addEventListener(
      "load",
      () => {
        script.dataset.status = "ready";
        resolve();
      },
      { once: true },
    );

    script.addEventListener(
      "error",
      () => {
        script.dataset.status = "error";
        reject(new Error(`Unable to load ${script.src}`));
      },
      { once: true },
    );
  });
}

function cssEscape(value: string) {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") return CSS.escape(value);

  return value.replace(/"/g, '\\"');
}
